import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Box, Modal, Typography, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Colors } from "../../../configs";
import { CustomButton } from "../../index";
import useSigWeb, { SIGWEB_CANVAS_W, SIGWEB_CANVAS_H } from "../../../hooks/useSigWeb";
import useSignaturePad from "../../../hooks/useSignaturePad";

// ─── Types ───────────────────────────────────────────────────────────────────

interface SignatureCaptureModalProps {
  open: boolean;
  onClose: () => void;
  onCapture: (signatureDataUrl: string) => void;
  agreementText?: string;
  onDisagree?: () => void;
}

type TopPhase = "agreement" | "capture";
type CaptureMode = "detecting" | "sigweb" | "pad" | "canvas";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CHARS_PER_PAGE = 500;

function computePages(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  if (trimmed.length <= CHARS_PER_PAGE) return [trimmed];
  const pages: string[] = [];
  let remaining = trimmed;
  while (remaining.length > CHARS_PER_PAGE) {
    let cut = remaining.lastIndexOf(" ", CHARS_PER_PAGE);
    if (cut <= 0) cut = CHARS_PER_PAGE;
    pages.push(remaining.slice(0, cut).trim());
    remaining = remaining.slice(cut).trim();
  }
  if (remaining) pages.push(remaining);
  return pages;
}

// ─────────────────────────────────────────────────────────────────────────────

const SignatureCaptureModal: React.FC<SignatureCaptureModalProps> = ({
  open,
  onClose,
  onCapture,
  agreementText,
  onDisagree,
}) => {
  const hasAgreement = Boolean(agreementText);

  // ── Top-level phase ───────────────────────────────────────────────────────
  const [topPhase, setTopPhase] = useState<TopPhase>(hasAgreement ? "agreement" : "capture");

  // ── Agreement pagination ──────────────────────────────────────────────────
  const pages = useMemo(() => computePages(agreementText ?? ""), [agreementText]);
  const [currentPage, setCurrentPage] = useState(0);
  const isLastPage = currentPage === pages.length - 1;

  // ── Capture mode ──────────────────────────────────────────────────────────
  const [mode, setMode] = useState<CaptureMode>("detecting");
  const [padPreview, setPadPreview] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);

  // ── Canvas fallback drawing ───────────────────────────────────────────────
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);
  const lastPos    = useRef<{ x: number; y: number } | null>(null);

  // ── SigWeb ────────────────────────────────────────────────────────────────
  // sigwebCanvasRef is only used during the CAPTURE phase for ink mirroring.
  // The agreement phase uses LCDWriteString direct writes — no canvas needed.
  const sigwebCanvasRef = useRef<HTMLCanvasElement>(null);
  const sigWeb          = useSigWeb();

  // true once SigWeb display mode is active and agreement text is on the LCD
  const sigwebDisplayActiveRef = useRef(false);
  const [sigwebDisplayActive, setSigwebDisplayActive] = useState(false);

  // ── Tray-app pad ──────────────────────────────────────────────────────────
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const {
    status: padStatus,
    startCapture: padStart,
    pollPreview,
    captureSignature,
    cancelCapture,
    reset,
  } = useSignaturePad();

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────

  const stopPadPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const startPadPolling = useCallback(() => {
    stopPadPolling();
    pollIntervalRef.current = setInterval(async () => {
      const r = await pollPreview();
      if (r.success) setPadPreview(r.signatureDataUrl);
    }, 500);
  }, [pollPreview, stopPadPolling]);

  const cleanupAll = useCallback(async () => {
    sigWeb.stopCapture();
    sigwebDisplayActiveRef.current = false;
    setSigwebDisplayActive(false);
    stopPadPolling();
    await cancelCapture();
    reset();
  }, [sigWeb, stopPadPolling, cancelCapture, reset]);

  // ─────────────────────────────────────────────────────────────────────────
  // Full reset on open/close
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) {
      cleanupAll();
      return;
    }
    setTopPhase(hasAgreement ? "agreement" : "capture");
    setCurrentPage(0);
    setMode("detecting");
    setPadPreview(null);
    setCapturing(false);
    setHasSigned(false);
    setIsDrawing(false);
    setSigwebDisplayActive(false);
    lastPos.current = null;
    sigwebDisplayActiveRef.current = false;
    const cv = canvasRef.current;
    if (cv) cv.getContext("2d")?.clearRect(0, 0, cv.width, cv.height);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ─────────────────────────────────────────────────────────────────────────
  // Agreement phase: start SigWeb display mode and push text to LCD
  // LCDWriteString makes direct HTTP calls to the SigWeb service — no canvas.
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open || topPhase !== "agreement" || !hasAgreement) return;
    let cancelled = false;
    (async () => {
      const available = await sigWeb.check();
      if (cancelled || !available) return;
      const ok = await sigWeb.startDisplayMode();
      if (cancelled || !ok) return;
      sigwebDisplayActiveRef.current = true;
      setSigwebDisplayActive(true);
      sigWeb.lcdWriteAgreementPage(pages[0], 0, pages.length);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, topPhase]);

  // ─────────────────────────────────────────────────────────────────────────
  // Capture phase: mode detection
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open || topPhase !== "capture" || mode !== "detecting") return;
    let cancelled = false;
    (async () => {
      // Fast path: SigWeb was active in agreement phase — switch LCD to ink mode
      if (sigwebDisplayActiveRef.current) {
        if (cancelled) return;
        sigWeb.switchToInkCapture();
        setMode("sigweb");
        return;
      }
      // Fresh SigWeb check
      const sigwebOk = await sigWeb.check();
      if (cancelled) return;
      if (sigwebOk) { setMode("sigweb"); return; }
      // Tray-app pad
      const padResult = await padStart();
      if (cancelled) return;
      if (padResult.success && padResult.available) {
        setMode("pad");
        startPadPolling();
        return;
      }
      // Canvas fallback
      setMode("canvas");
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, topPhase, mode]);

  // ─────────────────────────────────────────────────────────────────────────
  // SigWeb: start ink capture once canvas is mounted in capture phase.
  // Always called — even when coming from agreement display mode — because
  // startCapture sets up SetTabletState canvas mirroring that display mode
  // never initialises (it uses LCDWriteString directly instead).
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (mode !== "sigweb" || topPhase !== "capture" || !open) return;
    const canvas = sigwebCanvasRef.current;
    if (!canvas) return;
    sigWeb.startCapture(canvas);
    return () => { sigWeb.stopCapture(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, topPhase, open]);

  useEffect(() => {
    if (!open) stopPadPolling();
  }, [open, stopPadPolling]);

  // ─────────────────────────────────────────────────────────────────────────
  // Agreement page navigation
  // ─────────────────────────────────────────────────────────────────────────

  const handleNextPage = () => {
    const next = currentPage + 1;
    setCurrentPage(next);
    if (sigwebDisplayActiveRef.current) {
      sigWeb.lcdWriteAgreementPage(pages[next], next, pages.length);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Agreement actions
  // ─────────────────────────────────────────────────────────────────────────

  const handleDisagree = useCallback(async () => {
    await cleanupAll();
    onDisagree?.();
    onClose();
  }, [cleanupAll, onDisagree, onClose]);

  const handleAgree = useCallback(() => {
    setTopPhase("capture");
    setMode("detecting");
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Capture actions
  // ─────────────────────────────────────────────────────────────────────────

  const handleSigWebDone = useCallback(() => {
    const img = sigWeb.getImage();
    if (!img) return;
    sigWeb.stopCapture();
    sigwebDisplayActiveRef.current = false;
    onCapture(img);
  }, [sigWeb, onCapture]);

  const handlePadDone = useCallback(async () => {
    if (!padPreview || capturing) return;
    setCapturing(true);
    stopPadPolling();
    const r = await captureSignature();
    setCapturing(false);
    if (r.success && r.signatureDataUrl) onCapture(r.signatureDataUrl);
  }, [padPreview, capturing, stopPadPolling, captureSignature, onCapture]);

  const handleClose = useCallback(async () => {
    await cleanupAll();
    onClose();
  }, [cleanupAll, onClose]);

  // ─────────────────────────────────────────────────────────────────────────
  // Canvas fallback drawing
  // ─────────────────────────────────────────────────────────────────────────

  const getPos = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
    canvas: HTMLCanvasElement
  ) => {
    const rect = canvas.getBoundingClientRect();
    const sx   = canvas.width  / rect.width;
    const sy   = canvas.height / rect.height;
    if ("touches" in e) {
      return { x: (e.touches[0].clientX - rect.left) * sx, y: (e.touches[0].clientY - rect.top) * sy };
    }
    return { x: ((e as React.MouseEvent).clientX - rect.left) * sx, y: ((e as React.MouseEvent).clientY - rect.top) * sy };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    setIsDrawing(true);
    lastPos.current = getPos(e, canvas);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas || !lastPos.current) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = "round";
    ctx.lineJoin    = "round";
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setHasSigned(true);
  };

  const stopDrawing = () => { setIsDrawing(false); lastPos.current = null; };

  const handleClearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  };

  const handleCanvasCapture = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSigned) return;
    onCapture(canvas.toDataURL("image/png"));
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Shared button styles
  // ─────────────────────────────────────────────────────────────────────────

  const outlineBtn = {
    borderColor: Colors.MAIN_GRAY, color: Colors.MAIN_GRAY,
    borderRadius: "5px", px: 3, py: 0.8, fontSize: "var(--fs-base)",
  };

  const primaryBtn = {
    backgroundColor: Colors.PRIMARY, color: Colors.WHITE,
    borderRadius: "5px", px: 3.5, py: 0.8, fontSize: "var(--fs-base)",
    "&:hover": { backgroundColor: Colors.PRIMARY_DARK },
    "&.Mui-disabled": { backgroundColor: Colors.DISABLED_GREEN, color: Colors.WHITE },
  };

  const dangerBtn = {
    backgroundColor: "#d32f2f", color: Colors.WHITE,
    borderRadius: "5px", px: 3, py: 0.8, fontSize: "var(--fs-base)",
    "&:hover": { backgroundColor: "#b71c1c" },
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  const signed =
    (mode === "sigweb" && sigWeb.hasSignature) ||
    (mode === "canvas" && hasSigned);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableScrollLock
      disableRestoreFocus
      sx={{ "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,0.5)" } }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          bgcolor: Colors.WHITE,
          p: 3,
          borderRadius: "0.71rem",
          outline: "none",
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", top: 8, right: 8, color: Colors.MAIN_GRAY }}
        >
          <CloseIcon sx={{ fontSize: "1.4rem" }} />
        </IconButton>

        {/* ══════════════════════════════════════════════════════════════════
            AGREEMENT PHASE
            Agreement text is shown ONLY on the Topaz LCD via LCDWriteString.
            The browser shows a status indicator and the cashier controls.
        ══════════════════════════════════════════════════════════════════ */}
        {topPhase === "agreement" && (
          <>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5} pr={4}>
              <Box>
                <Typography fontWeight="bold" fontSize="var(--fs-md)" color={Colors.MAIN_GRAY}>
                  Agreement Required
                </Typography>
                <Typography fontSize="var(--fs-base)" color={Colors.MAIN_GRAY} mt={0.3}>
                  The agreement is displayed on the signature pad.
                </Typography>
              </Box>
              {pages.length > 1 && (
                <Typography fontSize="var(--fs-xs)" color={Colors.MAIN_GRAY} sx={{ mt: 0.5, whiteSpace: "nowrap" }}>
                  Page {currentPage + 1} of {pages.length}
                </Typography>
              )}
            </Box>

            {/* Pad status — agreement text lives on the Topaz LCD, not here */}
            <Box
              sx={{
                border: sigwebDisplayActive ? "1.5px solid #c8e6c9" : "1.5px solid #e0e0e0",
                borderRadius: "8px",
                bgcolor: sigwebDisplayActive ? "#f1f8f1" : Colors.BACKSHADE_GRAY,
                p: 2,
                mb: 1.5,
                minHeight: "140px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              {sigwebDisplayActive ? (
                <>
                  <Typography fontWeight="bold" fontSize="var(--fs-md)" color={Colors.PRIMARY} textAlign="center">
                    Agreement displayed on signature pad
                  </Typography>
                  <Typography fontSize="var(--fs-base)" color={Colors.MAIN_GRAY} textAlign="center">
                    Ask the customer to read the agreement on the pad,
                    then click <strong>Agree</strong> or <strong>Disagree</strong> below.
                  </Typography>
                </>
              ) : (
                <>
                  <CircularProgress size={24} sx={{ color: Colors.PRIMARY }} />
                  <Typography fontSize="var(--fs-base)" color={Colors.MAIN_GRAY} textAlign="center">
                    Connecting to signature pad…
                  </Typography>
                </>
              )}
            </Box>

            {/* Cashier controls */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
              <CustomButton variant="contained" onClick={handleDisagree} sx={dangerBtn}>
                Disagree
              </CustomButton>
              {!isLastPage ? (
                <CustomButton variant="contained" onClick={handleNextPage} sx={primaryBtn}>
                  Next
                </CustomButton>
              ) : (
                <CustomButton variant="contained" onClick={handleAgree} sx={primaryBtn}>
                  Agree
                </CustomButton>
              )}
            </Box>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            CAPTURE PHASE
        ══════════════════════════════════════════════════════════════════ */}
        {topPhase === "capture" && (
          <>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5} pr={4}>
              <Box>
                <Typography fontWeight="bold" fontSize="var(--fs-md)" color={Colors.MAIN_GRAY}>
                  Signature Required
                </Typography>
                <Typography fontSize="var(--fs-base)" color={Colors.MAIN_GRAY} mt={0.3}>
                  Customer must sign below.
                </Typography>
              </Box>
              {signed && (
                <Typography fontWeight="bold" fontSize="var(--fs-md)" color={Colors.PRIMARY} sx={{ whiteSpace: "nowrap", mt: 0.3 }}>
                  Signed
                </Typography>
              )}
            </Box>

            {/* ── Detecting ── */}
            {mode === "detecting" && (
              <>
                <Box
                  display="flex" flexDirection="column" alignItems="center" justifyContent="center"
                  sx={{ height: 120, bgcolor: Colors.BACKSHADE_GRAY, borderRadius: "8px", mb: 2 }}
                >
                  <CircularProgress size={28} sx={{ color: Colors.PRIMARY, mb: 1.5 }} />
                  <Typography fontSize="var(--fs-base)" color={Colors.MAIN_GRAY}>
                    Checking for signature pad…
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="flex-end">
                  <CustomButton variant="outlined" onClick={() => setMode("canvas")} sx={outlineBtn}>
                    Skip – Use On-Screen Signature
                  </CustomButton>
                </Box>
              </>
            )}

            {/* ── SigWeb (physical Topaz pad) ── */}
            {mode === "sigweb" && (
              <>
                <Box
                  sx={{
                    border: `1.5px solid ${sigWeb.hasSignature ? Colors.PRIMARY : "#ddd"}`,
                    borderRadius: "8px",
                    bgcolor: "#fff",
                    overflow: "hidden",
                    mb: 1.5,
                    position: "relative",
                  }}
                >
                  <canvas
                    ref={sigwebCanvasRef}
                    width={SIGWEB_CANVAS_W}
                    height={SIGWEB_CANVAS_H}
                    style={{ display: "block", width: "100%", height: "120px" }}
                  />
                  {!sigWeb.hasSignature && (
                    <Box
                      sx={{
                        position: "absolute", inset: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <Typography fontSize="var(--fs-base)" color={Colors.MAIN_GRAY}>
                        Sign on the pad…
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <CustomButton variant="outlined" onClick={handleClose} sx={outlineBtn}>
                    Cancel
                  </CustomButton>
                  <Box display="flex" gap={1.5}>
                    <CustomButton
                      variant="outlined"
                      disabled={!sigWeb.hasSignature}
                      onClick={() => sigWeb.clearPad(sigwebCanvasRef.current)}
                      sx={outlineBtn}
                    >
                      Clear
                    </CustomButton>
                    <CustomButton
                      variant="contained"
                      disabled={!sigWeb.hasSignature}
                      onClick={handleSigWebDone}
                      sx={primaryBtn}
                    >
                      Done
                    </CustomButton>
                  </Box>
                </Box>
              </>
            )}

            {/* ── Tray-app pad ── */}
            {mode === "pad" && (
              <>
                <Box
                  sx={{
                    height: 120,
                    border: `1.5px solid ${padPreview ? Colors.PRIMARY : "#ddd"}`,
                    borderRadius: "8px",
                    bgcolor: Colors.BACKSHADE_GRAY,
                    overflow: "hidden",
                    mb: 1.5,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  {padPreview ? (
                    <img
                      src={padPreview}
                      alt="signature preview"
                      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", padding: "8px" }}
                    />
                  ) : (
                    <Box display="flex" alignItems="center" gap={0.8}>
                      <CircularProgress size={14} sx={{ color: Colors.PRIMARY }} />
                      <Typography fontSize="var(--fs-base)" color={Colors.MAIN_GRAY}>Sign on the pad…</Typography>
                    </Box>
                  )}
                </Box>
                {padStatus === "waiting" && (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <CustomButton variant="outlined" onClick={handleClose} sx={outlineBtn}>
                      Cancel
                    </CustomButton>
                    <Box display="flex" gap={1.5}>
                      <CustomButton
                        variant="outlined"
                        disabled={!padPreview || capturing}
                        onClick={async () => {
                          if (!padPreview) return;
                          stopPadPolling();
                          await cancelCapture();
                          setPadPreview(null);
                          await padStart();
                          startPadPolling();
                        }}
                        sx={outlineBtn}
                      >
                        Clear
                      </CustomButton>
                      <CustomButton
                        variant="contained"
                        disabled={!padPreview || capturing}
                        onClick={handlePadDone}
                        sx={primaryBtn}
                      >
                        {capturing ? <CircularProgress size={16} sx={{ color: Colors.WHITE }} /> : "Done"}
                      </CustomButton>
                    </Box>
                  </Box>
                )}
                {padStatus === "captured" && (
                  <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                    <CheckCircleOutlineIcon sx={{ fontSize: "1.2rem", color: Colors.PRIMARY }} />
                    <Typography fontWeight="bold" fontSize="var(--fs-base)" color={Colors.PRIMARY}>
                      Signature captured!
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* ── Canvas fallback ── */}
            {mode === "canvas" && (
              <>
                <Box
                  sx={{
                    border: "1.5px dashed #ccc", borderRadius: "8px",
                    bgcolor: Colors.BACKSHADE_GRAY, overflow: "hidden",
                    mb: 2, cursor: "crosshair", userSelect: "none",
                  }}
                >
                  <canvas
                    ref={canvasRef}
                    width={900}
                    height={260}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    style={{ display: "block", width: "100%", height: "160px", touchAction: "none" }}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <CustomButton variant="outlined" onClick={handleClose} sx={outlineBtn}>
                    Cancel
                  </CustomButton>
                  <Box display="flex" gap={1.5}>
                    <CustomButton variant="outlined" onClick={handleClearCanvas} sx={outlineBtn}>
                      Clear
                    </CustomButton>
                    <CustomButton
                      variant="contained"
                      disabled={!hasSigned}
                      onClick={handleCanvasCapture}
                      sx={primaryBtn}
                    >
                      Done
                    </CustomButton>
                  </Box>
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default SignatureCaptureModal;
